import type { WorkingTreeChangeKind } from '../../types.js';
import { truncatePatch } from '../patch-truncate.js';
import { appendInjectedContextLines } from './append-injected-context-lines.js';
import { collectGapInjectLines } from './collect-gap-inject-lines.js';
import { consumeUnifiedHunkBody } from './consume-unified-hunk-body.js';
import { UNIFIED_HUNK_HEADER_RE } from './constants.js';
import { createHydrateSourceLines } from './create-hydrate-source-lines.js';
import { createHydrateYield } from './create-hydrate-yield.js';
import { updateHunkLineEnds } from './update-hunk-line-ends.js';

/**
 * Inserts space-prefixed context lines for ranges Git omitted between hunks, by reading the working
 * tree (or `HEAD` for deleted files) so the web UI can expand real content.
 */
export async function hydrateUnifiedPatchWithFileContext(
  patch: string,
  filePath: string,
  repoGitCwd: string,
  pathInRepo: string,
  change: WorkingTreeChangeKind,
): Promise<string> {
  if (!patch.trim() || patch.includes('Binary files')) return patch;

  const all = patch.split('\n');
  const out: string[] = [];
  let prevOldEnd = 0;
  let prevNewEnd = 0;
  const injectedTotal = { value: 0 };
  let i = 0;

  const maybeYield = createHydrateYield();
  const { blobLines, diskLines } = createHydrateSourceLines({ repoGitCwd, pathInRepo, filePath });

  while (i < all.length) {
    await maybeYield();
    const line = all[i]!;
    const hm = line.match(UNIFIED_HUNK_HEADER_RE);
    if (!hm) {
      out.push(line);
      i++;
      continue;
    }

    const oldStart = parseInt(hm[1]!, 10) || 0;
    const newStart = parseInt(hm[3]!, 10) || 0;

    const inject = await collectGapInjectLines({
      change,
      gapOldStart: prevOldEnd + 1,
      gapOldEnd: oldStart - 1,
      gapNewStart: prevNewEnd + 1,
      gapNewEnd: newStart - 1,
      injectedTotal: injectedTotal.value,
      blobLines,
      diskLines,
    });
    if (inject && inject.length > 0) {
      await appendInjectedContextLines(out, inject, maybeYield, injectedTotal);
    }

    out.push(line);
    i++;

    const body = await consumeUnifiedHunkBody({ all, startIndex: i, out, maybeYield });
    i = body.nextIndex;

    const ends = updateHunkLineEnds({
      oldStart,
      newStart,
      oldConsumed: body.oldConsumed,
      newConsumed: body.newConsumed,
    });
    prevOldEnd = ends.prevOldEnd;
    prevNewEnd = ends.prevNewEnd;
  }

  return truncatePatch(out.join('\n'));
}
